import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, BarChart2, History, Layers, FileText, ArrowRight } from 'lucide-react';
import { getRyanMedicalIcon } from '../constants/ryanMedicalIcons';
import './RyanHomeHero.css';

interface SavedSummary {
  folders: number;
  cards: number;
}

interface StatsSummary {
  mastery: number;
  streak: number;
  today: number;
  week: number;
}

interface LastSubdeck {
  deckId: string;
  subdeckId: string;
  name: string;
  iconId?: string | null;
  spaceId: string;
  spaceName: string;
  masteryPercentage: number;
}

interface LastApostila {
  deckId: string;
  subdeckId: string;
  name: string;
  iconId?: string | null;
  spaceId: string;
  spaceName: string;
  pageCount: number;
  readPercentage?: number;
}

interface RyanHomeHeroProps {
  /** Mesma troca de modo já usada pelo botão de alternância do topo (spaces → saved). */
  onViewSaved: () => void;
}

const RING_R = 30;
const RING_C = 2 * Math.PI * RING_R;

/**
 * Hero da página inicial do Ryan — três blocos de dados reais:
 * flashcards salvos (GET /api/ryan/saved/folders), continuar de onde parou
 * (GET /api/ryan/continue — último subdeck estudado + última apostila lida)
 * e estatísticas (GET /api/ryan/stats).
 */
const RyanHomeHero: React.FC<RyanHomeHeroProps> = ({ onViewSaved }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState<SavedSummary | null>(null);
  const [stats, setStats] = useState<StatsSummary | null>(null);
  const [lastSubdeck, setLastSubdeck] = useState<LastSubdeck | null>(null);
  const [lastApostila, setLastApostila] = useState<LastApostila | null>(null);
  const [ringProgress, setRingProgress] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };

    fetch('/api/ryan/saved/folders', { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data) setSaved({ folders: data.folders?.length ?? 0, cards: data.totalCards ?? 0 });
      })
      .catch(() => {});

    fetch('/api/ryan/stats', { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data) {
          setStats({
            mastery: data.totals?.masteryPercentage ?? 0,
            streak: data.streak?.current ?? 0,
            today: data.today?.cardsStudiedToday ?? 0,
            week: data.week?.cardsStudiedWeek ?? 0,
          });
        }
      })
      .catch(() => {});

    fetch('/api/ryan/continue', { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data) return;
        setLastSubdeck(data.lastSubdeck || null);
        setLastApostila(data.lastApostila || null);
      })
      .catch(() => {});
  }, []);

  // Dispara a animação do anel um instante depois do valor chegar, pra
  // sempre haver uma transição visível de 0 até o domínio real (em vez de
  // já nascer preenchido).
  useEffect(() => {
    if (stats == null) return;
    const t = setTimeout(() => setRingProgress(stats.mastery), 150);
    return () => clearTimeout(t);
  }, [stats]);

  const handleContinueSubdeck = (s: LastSubdeck) => {
    sessionStorage.setItem('graia_theme_context', 'ryan');
    navigate(`/study/${s.deckId}/${s.subdeckId}`, {
      state: {
        isPurchasedDeck: true,
        fromRyan: true,
        returnPath: `/ryan/campo/${s.spaceId}`,
        spaceName: s.spaceName,
        spaceId: s.spaceId,
      },
    });
  };

  const handleContinueApostila = (a: LastApostila) => {
    navigate(`/resumo/${a.deckId}/${a.subdeckId}`, {
      state: {
        isPurchasedDeck: true,
        fromRyan: true,
        returnPath: `/ryan/campo/${a.spaceId}`,
        spaceName: a.spaceName,
        spaceId: a.spaceId,
        isRyanEditor: false,
      },
    });
  };

  return (
    <div className="ryan-home-hero">
      <div className="rhh-card rhh-card--saved">
        <div className="rhh-top">
          <div className="rhh-icon rhh-icon--saved"><Bookmark size={20} /></div>
          <span className="rhh-title">Flashcards Salvos</span>
        </div>
        <div className="rhh-nums">
          <div className="rhh-num">
            <b>{saved ? saved.folders : '—'}</b>
            <span>Pasta{saved?.folders !== 1 ? 's' : ''}</span>
          </div>
          <div className="rhh-num">
            <b>{saved ? saved.cards : '—'}</b>
            <span>Cards salvos</span>
          </div>
        </div>
        <button className="rhh-btn" onClick={onViewSaved}>Ver flashcards salvos</button>
      </div>

      <div className="rhh-card rhh-card--continue">
        <div className="rhh-top">
          <div className="rhh-icon rhh-icon--continue"><History size={20} /></div>
          <span className="rhh-title">Continuar de onde parou</span>
        </div>

        <div className="rhh-continue-rows">
          {lastSubdeck && (() => {
            const DeckIcon = getRyanMedicalIcon(lastSubdeck.iconId) || Layers;
            return (
              <button className="rhh-continue-row" onClick={() => handleContinueSubdeck(lastSubdeck)}>
                <span className="rhh-continue-row-icon rhh-continue-row-icon--deck"><DeckIcon size={14} /></span>
                <span className="rhh-continue-row-text">
                  <span className="rhh-continue-row-name">{lastSubdeck.name}</span>
                  <span className="rhh-continue-row-meta">{lastSubdeck.masteryPercentage}% domínio</span>
                </span>
                <ArrowRight size={13} className="rhh-continue-row-arrow" />
              </button>
            );
          })()}

          {lastApostila && (() => {
            const ApostilaIcon = getRyanMedicalIcon(lastApostila.iconId) || FileText;
            return (
              <button className="rhh-continue-row" onClick={() => handleContinueApostila(lastApostila)}>
                <span className="rhh-continue-row-icon rhh-continue-row-icon--apostila"><ApostilaIcon size={14} /></span>
                <span className="rhh-continue-row-text">
                  <span className="rhh-continue-row-name">{lastApostila.name}</span>
                  <span className="rhh-continue-row-meta">
                    {lastApostila.readPercentage ?? 0}% lida · {lastApostila.pageCount} página{lastApostila.pageCount !== 1 ? 's' : ''}
                  </span>
                </span>
                <ArrowRight size={13} className="rhh-continue-row-arrow" />
              </button>
            );
          })()}

          {!lastSubdeck && !lastApostila && (
            <p className="rhh-continue-empty">Comece a estudar um deck pra ver seu progresso aqui.</p>
          )}
        </div>
      </div>

      <div className="rhh-card rhh-card--stats">
        <div className="rhh-stats-main">
          <div className="rhh-top">
            <div className="rhh-icon rhh-icon--stats"><BarChart2 size={20} /></div>
            <span className="rhh-title">Estatísticas</span>
          </div>
          <div className="rhh-nums rhh-nums--triple">
            <div className="rhh-num">
              <b>{stats ? stats.today : '—'}</b>
              <span>Hoje</span>
            </div>
            <div className="rhh-num">
              <b>{stats ? stats.week : '—'}</b>
              <span>Essa semana</span>
            </div>
            <div className="rhh-num">
              <b>{stats ? stats.streak : '—'}</b>
              <span>Dia{stats?.streak !== 1 ? 's' : ''} seguidos</span>
            </div>
          </div>
          <button
            className="rhh-btn"
            onClick={() => navigate('/ryan/estatisticas', { state: { returnPath: '/ryan' } })}
          >
            Ver estatísticas
          </button>
        </div>

        <div className="rhh-ring-wrap">
          <svg className="rhh-ring" viewBox="0 0 72 72">
            <circle className="rhh-ring-track" cx="36" cy="36" r={RING_R} />
            <circle
              className="rhh-ring-progress"
              cx="36"
              cy="36"
              r={RING_R}
              style={{
                strokeDasharray: RING_C,
                strokeDashoffset: RING_C * (1 - ringProgress / 100),
              }}
            />
          </svg>
          <div className="rhh-ring-center">
            <b>{stats ? `${Math.round(ringProgress)}%` : '—'}</b>
            <span>domínio</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RyanHomeHero;
